import { useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { AccountDetailModal } from './AccountDetailModal.js'
import { formatDate as formatDetailDate, Skeleton } from '../../core/index.js'
import type { AdminClientSummary } from '../../clients/index.js'
import { FacilityIcon, FacilityAdministratorIcon } from '../../core/index.js'
import { SortableTh, useSort, type SortAccessors } from '../../core/index.js'
import { NewFacilityAdministratorModal } from './NewFacilityAdministratorModal.js'
import { EmptyState, NewButton } from './formParts.js'
import type { ComponentProps } from 'react'
import type { AccountsAdminApi } from '../adminApi.js'
import type { ClientsAdminApi } from '../../clients/index.js'
import type { FacilitiesAdminApi } from '../../facilities/index.js'
import { LoadFailure, RefreshButton } from '../../core/index.js'

/** The admin calls this screen makes itself, and what the screens it opens need. */
type AdminApi = AccountsAdminApi & ClientsAdminApi & FacilitiesAdminApi & ComponentProps<typeof NewFacilityAdministratorModal>['api']


interface AdministratorRow {
  key: string
  name: string
  email: string
  phone: string | null
  client: AdminClientSummary
  /** Display names of the facilities they manage, falling back to the facilityId for one that can't be resolved. */
  facilityNames: string[]
  createdAt: string
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const ADMINISTRATOR_SORT: SortAccessors<AdministratorRow, 'name' | 'email' | 'client' | 'facilities' | 'created'> = {
  name: (row) => row.name,
  email: (row) => row.email,
  client: (row) => row.client.organizationName,
  facilities: (row) => row.facilityNames.join(', ') || null,
  created: (row) => row.createdAt,
}

/**
 * Every facility administrator (facility manager) across every client, one
 * row each, with their client and the facilities they manage. Like the
 * operators page, there is no single endpoint for this, so it asks each
 * active client for its administrators and facilities in parallel, on the
 * same cache keys the client panel uses.
 */
export function FacilityAdministratorList({ api }: { api: AdminApi }) {
  const [viewing, setViewing] = useState<AdministratorRow | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const clientsQuery = useQuery({ queryKey: ['admin', 'clients'], queryFn: () => api.listClients() })
  // Only an active client has a clientId, which is what administrators and facilities hang off.
  const activeClients = (clientsQuery.data ?? []).flatMap((client) => (client.clientId ? [{ client, clientId: client.clientId }] : []))

  const administratorQueries = useQueries({
    queries: activeClients.map(({ clientId }) => ({
      queryKey: ['admin', 'facility-administrators', clientId],
      queryFn: () => api.listFacilityAdministrators(clientId),
    })),
  })
  const facilityQueries = useQueries({
    queries: activeClients.map(({ clientId }) => ({
      queryKey: ['admin', 'facilities', clientId],
      queryFn: () => api.listFacilities(clientId),
    })),
  })

  const isLoading = clientsQuery.isLoading || administratorQueries.some((query) => query.isLoading) || facilityQueries.some((query) => query.isLoading)
  const isRefreshing = clientsQuery.isFetching || administratorQueries.some((query) => query.isFetching) || facilityQueries.some((query) => query.isFetching)
  const refresh = () => Promise.all([clientsQuery.refetch(), ...administratorQueries.map((query) => query.refetch()), ...facilityQueries.map((query) => query.refetch())])
  const failedClients = activeClients.filter((_, index) => administratorQueries[index]?.isError).map(({ client }) => client)

  const rows: AdministratorRow[] = activeClients
    .flatMap(({ client }, index): AdministratorRow[] => {
      const facilityNameById = new Map((facilityQueries[index]?.data ?? []).map((facility) => [facility.facilityId, facility.name]))
      return (administratorQueries[index]?.data ?? []).map((administrator) => ({
        key: administrator.administratorId,
        name: `${administrator.firstName} ${administrator.lastName}${administrator.invitationStatus === 'pending' ? ' · invited, waiting' : ''}`,
        email: administrator.email,
        phone: administrator.phone ?? null,
        client,
        facilityNames: administrator.facilityIds.map((facilityId) => facilityNameById.get(facilityId) ?? facilityId).sort((a, b) => a.localeCompare(b)),
        createdAt: administrator.createdAt,
      }))
    })
    .sort((a, b) => a.client.organizationName.localeCompare(b.client.organizationName) || a.name.localeCompare(b.name) || a.key.localeCompare(b.key))

  const { sorted, header } = useSort(rows, ADMINISTRATOR_SORT)

  return (
    <div className="max-w-5xl mx-auto px-7 py-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="text-sm text-ink-mid">
          {!isLoading &&
            !clientsQuery.isError &&
            `${rows.length} facility ${rows.length === 1 ? 'administrator' : 'administrators'} across ${activeClients.length} active ${activeClients.length === 1 ? 'client' : 'clients'}`}
        </div>
        <div className="flex items-center gap-2">
          <RefreshButton onRefresh={refresh} refreshing={isRefreshing} />
          <NewButton label="New facility administrator" onClick={() => setIsCreating(true)} />
        </div>
      </div>

      {failedClients.length > 0 && (
        <div className="border border-coral bg-coral-lt px-4 py-3 mb-4 text-xs text-ink">
          Couldn't load facility administrators for {failedClients.map((client) => client.organizationName).join(', ')}, so this list is incomplete.
        </div>
      )}

      {isLoading ? (
        <Skeleton lines={5} label="Loading facility administrators" />
      ) : clientsQuery.isError ? (
        <LoadFailure message="Failed to load clients. Confirm the API is reachable." onRetry={() => void clientsQuery.refetch()} retrying={clientsQuery.isFetching} />
      ) : rows.length === 0 ? (
        <EmptyState
          message="No facility administrators yet. Create one here, or a client's operators can add them from their own dashboard."
          actionLabel="Create a facility administrator"
          onAction={() => setIsCreating(true)}
        />
      ) : (
        <div className="bg-white border border-marine/15 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
                <SortableTh className="px-4 py-2" label="Facility administrator" {...header('name')} />
                <SortableTh className="px-4 py-2" label="Login email" {...header('email')} />
                <SortableTh className="px-4 py-2" label="Client" {...header('client')} />
                <SortableTh className="px-4 py-2" label="Facilities" {...header('facilities')} />
                <SortableTh className="px-4 py-2" label="Created" {...header('created')} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr key={row.key} className="border-b border-marine/10 align-top">
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2">
                    <FacilityAdministratorIcon className="w-4 h-4 text-marine" />
                    <button type="button" onClick={() => setViewing(row)} className="font-bold text-ink text-left underline decoration-marine/30 underline-offset-2 hover:text-marine hover:decoration-marine">
                      {row.name}
                    </button>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-mid">{row.email}</td>
                  <td className="px-4 py-3 text-ink">{row.client.organizationName}</td>
                  <td className="px-4 py-3 text-ink">
                    <ul className="space-y-0.5">
                      {row.facilityNames.map((facilityName) => (
                        <li key={facilityName} className="flex items-center gap-1.5">
                          <FacilityIcon className="w-3.5 h-3.5 text-marine" />
                          {facilityName}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-4 py-3 text-ink-mid whitespace-nowrap">{formatDate(row.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isCreating && <NewFacilityAdministratorModal api={api} onClose={() => setIsCreating(false)} />}
      {viewing && (
        <AccountDetailModal
          kind="Facility administrator"
          name={viewing.name}
          client={viewing.client}
          onClose={() => setViewing(null)}
          fields={[
            { label: 'Login email', value: viewing.email },
            { label: 'Phone', value: viewing.phone ?? 'Not recorded' },
            { label: 'Manages', value: viewing.facilityNames.length === 0 ? 'No facilities' : viewing.facilityNames.join(', ') },
            { label: 'Access', value: 'Only the facilities listed, not the rest of the client' },
            { label: 'Created', value: formatDetailDate(viewing.createdAt) },
          ]}
        />
      )}
    </div>
  )
}
